import { Injectable, inject } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  ApiException,
  ApiResponse,
  CatalogApplySuccessResponseDto,
  CatalogEventClient,
  CatalogEventSessionForFilterRequestDto,
  ApplyMassUpdateSeatSettingsRequest,
  ApplySeatSettingsRequest,
  CatalogPlanManagementCategoryClient,
  CatalogPlanManagementContingentClient,
  CatalogPlanManagementSeatClient,
  CatalogPlanManagementUnavailabilityClient,
  CatalogPlanSeatResponseDto,
  CatalogSeatsClient,
  EventPlanManagementBlockClient,
  EventPlanManagementBlockDetailsClient,
  EventPlanManagementClient,
  EventPlanManagementDetailsClient,
  EventSessionMultiSelectionStatsResponseDto,
  EventSessionPlanBlockFilterRequestDto,
  EventSessionPlanBlockFilterResponseDto,
  EventSessionPlanBlockInformationsResponseDto,
  EventSessionPlanBlockResponseDto,
  EventSessionPlanCategoryResponseDto,
  EventSessionPlanContingentImageResponseDto,
  EventSessionPlanContingentResponseDto,
  EventSessionPlanHeaderResponseDto,
  EventSessionPlanImageResponseDto,
  EventSessionPlanLetterResponseDto,
  EventSessionPlanSeatDetailsResponseDto,
  EventSessionPlanShapeResponseDto,
  EventSessionPlanUnavailableSeatTypesResponseDto,
  EventSessionStatsResponseDto,
  EventSessionVenueInformationLegendResponseDto,
  EventSessionVenueInformationsLegendClient,
  FileResponse,
  GetModificationPreviewRequest,
  GetModificationPreviewResponse,
  CatalogModificationPreviewResponseDto,
  PaginatedResultOfCatalogEventSessionWithSamePlanResponseDto,
  SeatClient,
} from '@core/services/apiclient';
import { HttpHandlerService } from '@core/services/http-handler.service';
import { Observable, map } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class CatalogPlanService {
  private readonly eventPlanManagementClient = inject(EventPlanManagementClient);
  private readonly eventPlanManagementBlockClient = inject(EventPlanManagementBlockClient);
  private readonly legendClient = inject(EventSessionVenueInformationsLegendClient);
  private readonly eventPlanManagementBlockDetailsClient = inject(EventPlanManagementBlockDetailsClient);
  private readonly eventPlanManagementDetails = inject(EventPlanManagementDetailsClient);
  private readonly catalogSeatsClient = inject(CatalogSeatsClient);
  private readonly catalogPlanManagementSeatClient = inject(CatalogPlanManagementSeatClient);
  private readonly seatClient = inject(SeatClient);
  private readonly httpHandlerService = inject(HttpHandlerService);
  private readonly catalogPlanManagementContingentClient = inject(CatalogPlanManagementContingentClient);
  private readonly catalogPlanManagementCategoryClient = inject(CatalogPlanManagementCategoryClient);
  private readonly catalogPlanManagementUnavailabilityClient = inject(
    CatalogPlanManagementUnavailabilityClient,
  );
  private readonly catalogEventClient = inject(CatalogEventClient);

  private readonly stampVersionHeader = 'etag';

  getCatalogEventDetails(eventSessionId: number): Observable<EventSessionPlanHeaderResponseDto> {
    return this.eventPlanManagementClient
      .getCatalogEventHeaderByEventSessionId(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanHeaderResponseDto>());
  }

  getTopviewPlanBlocks(eventSessionId: number): Observable<EventSessionPlanBlockResponseDto[]> {
    return this.eventPlanManagementBlockClient
      .getCatalogEventBlock(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanBlockResponseDto[]>());
  }

  getCatalogEventBackGround(eventSessionId: number): Observable<FileResponse> {
    return this.eventPlanManagementClient
      .getCatalogEventBackGroundFile(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<FileResponse>());
  }

  getEventSessionVenueInformationLegend(
    eventSessionId: number,
  ): Observable<EventSessionVenueInformationLegendResponseDto> {
    return this.legendClient
      .getEventSessionVenueInformationLegend(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionVenueInformationLegendResponseDto>());
  }

  getEventSessionPlanContingents(
    eventSessionId: number,
    categoryIds: number[],
    unavailableTypeIds: number[],
    seatStatuses?: string[],
  ): Observable<EventSessionPlanContingentResponseDto[]> {
    return this.catalogPlanManagementContingentClient
      .getEventSessionPlanContingents(eventSessionId, categoryIds, unavailableTypeIds, seatStatuses?.length ? seatStatuses : undefined, undefined, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanContingentResponseDto[]>());
  }
  getEventSessionPlanCategories(
    eventSessionId: number,
    contingentIds: number[],
    unavailableTypeIds: number[],
    seatStatuses?: string[],
  ): Observable<EventSessionPlanCategoryResponseDto[]> {
    return this.catalogPlanManagementCategoryClient
      .getEventSessionPlanCategories(eventSessionId, contingentIds, unavailableTypeIds, seatStatuses?.length ? seatStatuses : undefined, undefined, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanCategoryResponseDto[]>());
  }
  getEventSessionPlanUnavailableSeatTYpes(
    eventSessionId: number,
    contingentIds: number[],
    categoryIds: number[],
    seatStatuses?: string[],
  ): Observable<EventSessionPlanUnavailableSeatTypesResponseDto[]> {
    return this.catalogPlanManagementUnavailabilityClient
      .getEventSessionPlanUnavailableSeats(eventSessionId, contingentIds, categoryIds, seatStatuses?.length ? seatStatuses : undefined, undefined, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanUnavailableSeatTypesResponseDto[]>());
  }

  applyTopViewFilter(
    eventSessionId: number,
    contingentsFilter: number[],
    categoriesFilter: number[],
    unavailableSeatTypesFilter: number[],
    seatStatusesFilter?: string[],
  ): Observable<EventSessionPlanBlockFilterResponseDto[]> {
    return this.eventPlanManagementBlockClient
      .blockFilter(
        {
          eventSessionId,
          contingentIds: contingentsFilter,
          categoryIds: categoriesFilter,
          unavailableTypes: unavailableSeatTypesFilter,
          seatStatuses: seatStatusesFilter?.length ? seatStatusesFilter : undefined,
        },
        Constants.ApiVersion,
      )
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanBlockFilterResponseDto[]>());
  }

  private getSeatsWithStampVersion<T>(fetchSeats: () => Observable<ApiResponse<T>>): Observable<[T, string]> {
    return fetchSeats().pipe(
      map((response) => {
        if (response.status === 200) {
          return [response.result, this.trimDoubleQuotes(response.headers[this.stampVersionHeader])];
        } else {
          throw new ApiException('no result', response.status, 'no result', response.headers, null);
        }
      }),
    );
  }

  private trimDoubleQuotes(input: string): string {
    return input.replace(/^"|"$/g, '');
  }

  getBlockViewSeats(
    eventSessionId: number,
    blockId: number,
  ): Observable<[CatalogPlanSeatResponseDto[], string]> {
    return this.getSeatsWithStampVersion(() =>
      this.catalogSeatsClient.catalogGetEventSessionPlanBlockSeats(
        eventSessionId,
        blockId,
        null,
        Constants.ApiVersion,
      ),
    );
  }

  getDifferentialBlockViewSeats(
    eventSessionId: number,
    blockId: number,
    stampVersion: string,
  ): Observable<[CatalogPlanSeatResponseDto[], string]> {
    return this.getSeatsWithStampVersion(() =>
      this.catalogSeatsClient.catalogGetEventSessionPlanBlockSeats(
        eventSessionId,
        blockId,
        `"${stampVersion}"`,
        Constants.ApiVersion,
      ),
    );
  }

  getExcludingMultiBlocksSeats(eventSessionId: number): Observable<[CatalogPlanSeatResponseDto[], string]> {
    return this.getSeatsWithStampVersion(() =>
      this.catalogSeatsClient.catalogGetEventSessionPlanSeats(eventSessionId, null, Constants.ApiVersion),
    );
  }

  getDifferentialExcludingMultiBlocksSeats(
    eventSessionId: number,
    stampVersion: string,
  ): Observable<[CatalogPlanSeatResponseDto[], string]> {
    return this.getSeatsWithStampVersion(() =>
      this.catalogSeatsClient.catalogGetEventSessionPlanSeats(
        eventSessionId,
        `"${stampVersion}"`,
        Constants.ApiVersion,
      ),
    );
  }

  getBlockViewLetters(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanLetterResponseDto[]> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanLetters(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanLetterResponseDto[]>());
  }

  getBlockViewContingentsImages(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionContingentImages(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanContingentImageResponseDto[]>());
  }

  getAllContingentsImages(): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return this.catalogPlanManagementContingentClient
      .getAllPlanContingentsImages(Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanContingentImageResponseDto[]>());
  }

  getBlockViewImages(eventSessionId: number, blockId: number): Observable<EventSessionPlanImageResponseDto> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanImages(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanImageResponseDto>());
  }

  getBlockViewShapes(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanShapeResponseDto[]> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanShapes(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanShapeResponseDto[]>());
  }

  getExcludingMultiBlocksLetters(eventSessionId: number): Observable<EventSessionPlanLetterResponseDto[]> {
    return this.eventPlanManagementDetails
      .getEventSessionPlanLetters(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanLetterResponseDto[]>());
  }

  getExcludingMultiBlocksImages(eventSessionId: number): Observable<EventSessionPlanImageResponseDto> {
    return this.eventPlanManagementDetails
      .getEventSessionPlanImages(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanImageResponseDto>());
  }

  getExcludingMultiBlocksContingentsImages(
    eventSessionId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return this.eventPlanManagementDetails
      .getEventSessionContingentImages(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanContingentImageResponseDto[]>());
  }

  getExcludingMultiBlocksShapes(eventSessionId: number): Observable<EventSessionPlanShapeResponseDto[]> {
    return this.eventPlanManagementDetails
      .getEventSessionPlanShapes(eventSessionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanShapeResponseDto[]>());
  }

  getBlockTooltip(
    eventSessionId: number,
    blockId: number,
  ): Observable<EventSessionPlanBlockInformationsResponseDto> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanBlockInformations(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanBlockInformationsResponseDto>());
  }

  getAllCategories(
    eventSessionId: number,
    blockId?: number,
  ): Observable<EventSessionPlanCategoryResponseDto[]> {
    return this.catalogPlanManagementCategoryClient
      .getAllEventSessionPlanCategories(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanCategoryResponseDto[]>());
  }

  getAllContingents(
    eventSessionId: number,
    blockId?: number,
  ): Observable<EventSessionPlanContingentResponseDto[]> {
    return this.catalogPlanManagementContingentClient
      .getAllEventSessionPlanContingents(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanContingentResponseDto[]>());
  }

  getAllUnavailableSeatsList(
    eventSessionId: number,
    blockId?: number,
  ): Observable<EventSessionPlanUnavailableSeatTypesResponseDto[]> {
    return this.catalogPlanManagementUnavailabilityClient
      .getAllEventSessionPlanUnavailableSeats(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanUnavailableSeatTypesResponseDto[]>());
  }

  getCatalogEventSessionStats(
    eventSessionId: number,
    blockId?: number,
  ): Observable<EventSessionStatsResponseDto> {
    return this.eventPlanManagementClient
      .getCatalogEventSessionStats(eventSessionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionStatsResponseDto>());
  }

  applySeatSettings(
    request: ApplySeatSettingsRequest,
  ): Observable<ApiResponse<CatalogApplySuccessResponseDto>> {
    const response = this.catalogPlanManagementSeatClient.catalogPlanManagementApplySeatSettings(
      request,
      Constants.ApiVersion,
    );

    return response;
  }

  applyTopViewMultiQuickFilter(
    blockFilterRequest: EventSessionPlanBlockFilterRequestDto,
  ): Observable<EventSessionPlanBlockFilterResponseDto[]> {
    return this.eventPlanManagementBlockClient
      .blockFilter(blockFilterRequest, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanBlockFilterResponseDto[]>());
  }

  public getTooltipSeatInfo(seatId: number): Observable<EventSessionPlanSeatDetailsResponseDto> {
    return this.seatClient
      .getEventSessionPlanSeatDetails(seatId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionPlanSeatDetailsResponseDto>());
  }

  public getBlockName(blockId: number): Observable<string> {
    return this.eventPlanManagementBlockDetailsClient
      .getEventSessionPlanBlockName(blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<string>());
  }

  getCatalogEventSessionMultiSelectionStats(
    eventSessionId: number,
    contingentIds: number[],
    categoryIds: number[],
    unavailableIds: number[],
    blockId: number,
    seatStatusIds: string[],
  ): Observable<EventSessionMultiSelectionStatsResponseDto> {
    return this.eventPlanManagementClient
      .getCatalogEventSessionMultiSelectionStats(
        eventSessionId,
        contingentIds,
        categoryIds,
        unavailableIds,
        blockId,
        seatStatusIds,
        Constants.ApiVersion,
      )
      .pipe(this.httpHandlerService.handleApiResponse<EventSessionMultiSelectionStatsResponseDto>());
  }
  applyMassUpdateSeatSettings(
    request: ApplyMassUpdateSeatSettingsRequest,
  ): Observable<ApiResponse<CatalogApplySuccessResponseDto>> {
    return this.catalogPlanManagementSeatClient.catalogPlanManagementApplyMassUpdateSeatSettings(
      request,
      Constants.ApiVersion,
    );
  }

  getEventsWithSamePlan(
    eventSessionId: number,
    filter: CatalogEventSessionForFilterRequestDto,
  ): Observable<PaginatedResultOfCatalogEventSessionWithSamePlanResponseDto> {
    return this.catalogEventClient
      .autocompleteEventsWithSamePlan(eventSessionId, filter, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<PaginatedResultOfCatalogEventSessionWithSamePlanResponseDto>(),
      );
  }

  getModificationPreview(
    request: GetModificationPreviewRequest,
  ): Observable<CatalogModificationPreviewResponseDto> {
    return this.catalogPlanManagementSeatClient.getModificationPreview(request, Constants.ApiVersion).pipe(
      this.httpHandlerService.handleApiResponse<GetModificationPreviewResponse>(),
      map((response) => response.preview!),
    );
  }
}
